import { DBSchema, deleteDB, openDB } from 'idb'

import type {
  DriverStatusRequestDto,
  OperationsRequestDto,
} from '@/services/Operation/OperationService.dto'

export type QueuedMutation =
  | { endpoint: 'sendChangeDriverStatus'; args: DriverStatusRequestDto }
  | { endpoint: 'sendOperations'; args: OperationsRequestDto }

interface QueuedMutationValue {
  id?: number
  ownerId: string
  mutation: QueuedMutation
  createdAt: string
  attempts: number
  nextAttemptAt: string | null
}

export interface QueuedMutationRecord extends QueuedMutationValue {
  id: number
}

interface OfflineDB extends DBSchema {
  mutationQueue: {
    key: number
    value: QueuedMutationValue
  }
}

const DB_NAME = 'tcsofor-offline-db'
const STORE = 'mutationQueue'
export const MUTATION_QUEUE_CHANGED_EVENT = 'offline-mutation-queue-changed'
// v3 discards v2 records because they cannot be safely attributed to an authenticated user.
const DB_VERSION = 3
const LEGACY_STORE = 'tcsoforQueue'

const openOfflineDb = () =>
  openDB<OfflineDB>(DB_NAME, DB_VERSION, {
    upgrade(db, oldVersion) {
      if (db.objectStoreNames.contains(LEGACY_STORE as never)) {
        db.deleteObjectStore(LEGACY_STORE as never)
      }
      if (oldVersion < DB_VERSION && db.objectStoreNames.contains(STORE)) {
        db.deleteObjectStore(STORE)
      }
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'id', autoIncrement: true })
      }
    },
  })

// A DB without the queue store has no recoverable queue records, so recreate it.
const getDb = async () => {
  const db = await openOfflineDb()
  if (db.objectStoreNames.contains(STORE)) return db
  db.close()
  await deleteDB(DB_NAME)
  return openOfflineDb()
}

const notifyQueueChanged = () => window.dispatchEvent(new Event(MUTATION_QUEUE_CHANGED_EVENT))

const hasRecordId = (record: QueuedMutationValue): record is QueuedMutationRecord =>
  typeof record.id === 'number'

export const enqueueMutation = async (ownerId: string, mutation: QueuedMutation): Promise<void> => {
  const db = await getDb()
  try {
    await db.add(STORE, {
      ownerId,
      mutation,
      createdAt: new Date().toISOString(),
      attempts: 0,
      nextAttemptAt: null,
    })
  } finally {
    db.close()
  }
  notifyQueueChanged()
}

export const getQueuedMutations = async (ownerId: string): Promise<QueuedMutationRecord[]> => {
  const db = await getDb()
  try {
    return (await db.getAll(STORE))
      .filter(hasRecordId)
      .filter((record) => record.ownerId === ownerId)
  } finally {
    db.close()
  }
}

export const removeQueuedMutation = async (id: number): Promise<void> => {
  const db = await getDb()
  try {
    await db.delete(STORE, id)
  } finally {
    db.close()
  }
  notifyQueueChanged()
}

export const scheduleQueuedMutationRetry = async (
  record: QueuedMutationRecord,
  nextAttemptAt: string
): Promise<void> => {
  const db = await getDb()
  try {
    await db.put(STORE, {
      ...record,
      attempts: record.attempts + 1,
      nextAttemptAt,
    })
  } finally {
    db.close()
  }
  notifyQueueChanged()
}

// A shared device can hold records queued by a previous driver. They must never
// replay under the new session's token, so drop them at login.
export const removeQueuedMutationsForOtherOwners = async (ownerId: string): Promise<void> => {
  const db = await getDb()
  try {
    const stale = (await db.getAll(STORE))
      .filter(hasRecordId)
      .filter((record) => record.ownerId !== ownerId)
    await Promise.all(stale.map((record) => db.delete(STORE, record.id)))
  } finally {
    db.close()
  }
  notifyQueueChanged()
}

export const clearMutationQueue = async (): Promise<void> => {
  const db = await getDb()
  try {
    await db.clear(STORE)
  } finally {
    db.close()
  }
  notifyQueueChanged()
}

// A request that never reached the server (offline / timeout) — the cue to queue it
// for replay, as opposed to a real error the server returned.
export const isOfflineError = (error: unknown): boolean => {
  const status = (error as { status?: unknown } | null | undefined)?.status
  return status === 'FETCH_ERROR' || status === 'TIMEOUT_ERROR'
}

export const isPermanentMutationError = (error: unknown): boolean => {
  const status = (error as { status?: unknown } | null | undefined)?.status
  return (
    typeof status === 'number' && status >= 400 && status < 500 && status !== 408 && status !== 429
  )
}
